// Queries y mutations de TanStack Query.
//
// staleTime sensato por endpoint:
//   - contexto-lider: 60s (cambia cuando el coordinador re-programa)
//   - skus por línea: 5min (configuración estable)
//   - causales-paro: Infinity (datos muy estables, refetch on focus = off)
//   - registros/turno-hoy: refetchInterval 30s (el supervisor puede delegar)

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  causalParoTreeNodeSchema,
  contextoLiderSchema,
  verComoRosterSchema,
  categoriaParoOutputSchema,
  type CategoriaParoOutput,
  type CrearCategoriaParo,
  type ActualizarCategoriaParo,
  tiendaSchema,
  tiendaCreateSchema,
  tiendaSkuSchema,
  tiendaSkuCatalogoSchema,
  tiendaSkuCreateSchema,
  type TiendaCreate,
  type TiendaUpdate,
  type TiendaSkuCreate,
  type TiendaSkuUpdate,
  registroTiendaSchema,
  registrosTiendaBulkCreateSchema,
  registroTiendaEditarSchema,
  registroTiendaInvalidarSchema,
  type RegistrosTiendaBulkCreate,
  type RegistroTiendaEditar,
  type RegistroTiendaInvalidar,
  coordinadorAnalisisLineaParosSchema,
  coordinadorAnalisisLineasSchema,
  coordinadorAnalisisParosSchema,
  coordinadorAnalisisPersonalSchema,
  coordinadorEntradaPitsSchema,
  coordinadorMetasPitsSchema,
  metasPitsPutResultSchema,
  type EntradaPitsQuery,
  type MetasPitsPut,
  coordinadorPersonalPerfilSchema,
  coordinadorResumenSemanalSchema,
  coordinadorSkusListSchema,
  coordinadorSkuDetalleSchema,
  coordinadorSkusMatrizSchema,
  costoManoObraResponseSchema,
  costoInsumoResponseSchema,
  precioBulkResultSchema,
  skuLineasBulkResultSchema,
  coordinadorTurnosListSchema,
  horarioSemanaGetSchema,
  horarioSemanaPutResponseSchema,
  type HorarioSemanaPut,
  coordinadorUsuariosListSchema,
  coordinadorAuditoriaSchema,
  coordinadorCumplimientoLineaSchema,
  coordinadorCumplimientoSchema,
  coordinadorDashboardSchema,
  coordinadorLineaDetalleSchema,
  coordinadorLineaTiempoSchema,
  type CoordinadorLineaTiempoQuery,
  directivoDashboardSchema,
  directivoResumenSchema,
  directivoResumenDetalleSchema,
  directivoLineasSchema,
  directivoFacturacionSchema,
  directivoPersonasSchema,
  type GranularidadDetalle,
  directivoResumenSemanalSchema,
  directivoAnalisisMensualSchema,
  directivoAnalisisClienteSchema,
  adminTenantSchema,
  adminClientesListSchema,
  adminCausalesListSchema,
  adminEstructuraSchema,
  adminUsuariosListSchema,
  superTenantsListSchema,
  superTenantDetalleSchema,
  superTenantUsuariosSchema,
  meResponseSchema,
  superSaludSchema,
  superAuditoriaSchema,
  coordinadorProgramacionProduccionSemanaSchema,
  coordinadorProgramacionSemanaSchema,
  coordinadorProgramacionSupervisoresSemanaSchema,
  copiarSemanaInputSchema,
  copiarSemanaResponseSchema,
  pedidoExtraCreateSchema,
  pedidosExtraListSchema,
  programacionConDetallesSchema,
  programacionCreateSchema,
  programacionDiffSchema,
  programacionOpListSchema,
  programacionOpCreateSchema,
  programacionOpImportSchema,
  programacionOpImportResultSchema,
  programacionOpCorreccionSchema,
  type ProgramacionOpListQuery,
  type ProgramacionOpCreate,
  type ProgramacionOpImport,
  type ProgramacionOpCorreccion,
  facturacionCruceSchema,
  cruceDetalleSchema,
  cruceCerrarSchema,
  cruceCerrarResultSchema,
  cruceReabrirSchema,
  type FacturacionCruceQuery,
  type CruceDetalleQuery,
  type CruceCerrar,
  type CruceReabrir,
  nivelServicioSchema,
  type NivelServicioQuery,
  facturacionQSchema,
  facturacionQDominicalSchema,
  facturacionQDominicalResultSchema,
  type FacturacionQQuery,
  type FacturacionQDominical,
  programacionLiderCreateSchema,
  programacionLiderEnriquecidaSchema,
  programacionLiderPatchSchema,
  programacionSupervisorCreateSchema,
  programacionSupervisorEnriquecidaSchema,
  programacionSupervisoresCopiarInputSchema,
  programacionSupervisoresCopiarResponseSchema,
  type ProgramacionSupervisoresCopiarInput,
  paroSchema,
  parosAbiertosListSchema,
  parosCerradosPorMttoListSchema,
  registroBloqueCreateSchema,
  registroProduccionHoraSchema,
  correccionRegistroSchema,
  altaRetroactivaRegistroSchema,
  type CorreccionRegistro,
  type AltaRetroactivaRegistro,
  editarRegistroSchema,
  historialRegistrosSchema,
  skuConCapacidadSchema,
  supervisorDashboardSchema,
  supervisorHistoricoSchema,
  supervisorIncidenciaDetalleSchema,
  supervisorIncidenciaItemSchema,
  supervisorIncidenciaResueltaSchema,
  supervisorIncidenciasSchema,
  supervisorLineaDetalleSchema,
  supervisorMisAccionesSchema,
  supervisorPendientesSchema,
  supervisorResolverMasivoResultSchema,
  supervisorResolverMasivoSchema,
  supervisorResumenHoraSchema,
  type SupervisorIncidenciasQuery,
  type SupervisorResolverMasivo,
  type AnalisisLineasQuery,
  type AnalisisParosQuery,
  type AnalisisPersonalQuery,
  type ResumenSemanalQuery,
  type SkusListQuery,
  type SkuCreate,
  type SkuUpdate,
  type SkuLineaUpsert,
  type PrecioUpsert,
  type PrecioBulk,
  type CostoManoObraUpsert,
  type CostoInsumoUpsert,
  type SkuLineasBulk,
  type TurnoCreate,
  type TurnoUpdate,
  type UsuarioCreate,
  type UsuarioUpdate,
  type UsuarioResetPassword,
  type UsuarioReasignarLineas,
  type AuditoriaQuery,
  type CumplimientoQuery,
  type PedidoExtraCreate,
  type ProgramacionCreate,
  type ProgramacionLiderCreate,
  type ProgramacionLiderPatch,
  type CopiarSemanaInput,
  type ProgramacionSupervisorCreate,
  type RegistroBloqueCreate,
  bloqueRegistradoSchema,
  type EditarRegistro,
  type HistorialRegistrosQuery,
} from 'schemas';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';
import { intervaloCaptura, intervaloSupervisor } from './intervalo-vivo';

export const queryKeys = {
  contextoLider: () => ['contexto-lider'] as const,
  skusByLinea: (lineaId: string) => ['lineas', lineaId, 'skus'] as const,
  causalesParo: () => ['causales-paro'] as const,
  registrosTurnoHoy: (turnoId: string) => ['registros', 'turno-hoy', turnoId] as const,
  tiendas: (incluirInactivas = false) => ['tiendas', incluirInactivas] as const,
  tiendaSkus: () => ['tiendas', 'skus'] as const,
  tiendaCapacidades: () => ['tiendas', 'capacidades'] as const,
  registrosTiendaHoy: (tiendaId: string) => ['registros-tienda', 'hoy', tiendaId] as const,
  registrosHistorial: (q: HistorialRegistrosQuery) => ['registros', 'historial', q] as const,
  parosAbiertos: (turnoId: string) => ['paros', 'abiertos', turnoId] as const,
  parosCerradosPorMtto: (turnoId: string) => ['paros', 'cerrados-por-mtto', turnoId] as const,
  supervisorDashboard: () => ['supervisor', 'dashboard'] as const,
  supervisorLineaDetalle: (lineaId: string) => ['supervisor', 'linea', lineaId] as const,
  supervisorHistorico: (periodo: string) => ['supervisor', 'historico', periodo] as const,
  supervisorMisAcciones: (dias: number) => ['supervisor', 'mis-acciones', dias] as const,
  coordinadorProgramacionSemana: (semanaIso: string | null) =>
    ['coordinador', 'programacion-lideres', semanaIso ?? 'actual'] as const,
  coordinadorProgramacionSupervisores: (semanaIso: string | null) =>
    ['coordinador', 'programacion-supervisores', semanaIso ?? 'actual'] as const,
  coordinadorProgramaciones: (semanaIso: string | null) =>
    ['coordinador', 'programaciones', semanaIso ?? 'actual'] as const,
  coordinadorProgramacion: (id: string) => ['coordinador', 'programacion', id] as const,
  coordinadorProgramacionDiff: (from: string, to: string) =>
    ['coordinador', 'programaciones-diff', from, to] as const,
  coordinadorPedidosExtra: (semanaIso: string | null) =>
    ['coordinador', 'pedidos-extra', semanaIso ?? 'actual'] as const,
  coordinadorCumplimiento: (q: CumplimientoQuery) => ['coordinador', 'cumplimiento', q] as const,
  coordinadorCumplimientoLinea: (lineaId: string, desde: string | null, hasta: string | null) =>
    ['coordinador', 'cumplimiento-linea', lineaId, desde, hasta] as const,
  coordinadorAnalisisLineas: (q: AnalisisLineasQuery) =>
    ['coordinador', 'analisis-lineas', q] as const,
  coordinadorAnalisisLineaParos: (lineaId: string, desde: string | null, hasta: string | null) =>
    ['coordinador', 'analisis-lineas', lineaId, 'paros', desde, hasta] as const,
  coordinadorAnalisisParos: (q: AnalisisParosQuery) =>
    ['coordinador', 'analisis-paros', q] as const,
  coordinadorAnalisisPersonal: (q: AnalisisPersonalQuery) =>
    ['coordinador', 'analisis-personal', q] as const,
  coordinadorEntradaPits: (q: EntradaPitsQuery) => ['coordinador', 'entrada-pits', q] as const,
  coordinadorMetasPits: () => ['coordinador', 'configuracion', 'metas-pits'] as const,
  coordinadorResumenSemanal: (q: ResumenSemanalQuery) =>
    ['coordinador', 'resumen-semanal', q] as const,
  coordinadorSkusList: (q: SkusListQuery) => ['coordinador', 'configuracion', 'skus', q] as const,
  coordinadorSkuDetalle: (id: string) => ['coordinador', 'configuracion', 'skus', id] as const,
  coordinadorSkusMatriz: () => ['coordinador', 'configuracion', 'skus', 'matriz'] as const,
  coordinadorCostoManoObra: () => ['coordinador', 'configuracion', 'costo-mano-obra'] as const,
  coordinadorCostoInsumo: () => ['coordinador', 'configuracion', 'costo-insumo'] as const,
  coordinadorTurnosList: () => ['coordinador', 'configuracion', 'turnos'] as const,
  coordinadorHorarioSemana: (turnoId: string) =>
    ['coordinador', 'configuracion', 'turnos', turnoId, 'horario-semana'] as const,
  coordinadorUsuariosList: () => ['coordinador', 'configuracion', 'usuarios'] as const,
  coordinadorAuditoria: (q: AuditoriaQuery) => ['coordinador', 'auditoria', q] as const,
  coordinadorDashboard: (q: {
    cadencia?: 'hoy' | 'semana' | 'mes';
    desde?: string;
    hasta?: string;
  }) => ['coordinador', 'dashboard', q] as const,
  coordinadorLineaDetalle: (lineaId: string, turnoId: string, fecha: string | null) =>
    ['coordinador', 'linea-detalle', lineaId, turnoId, fecha] as const,
  coordinadorLineaTiempo: (lineaId: string, q: CoordinadorLineaTiempoQuery) =>
    ['coordinador', 'linea-tiempo', lineaId, q] as const,
  coordinadorPersonalPerfil: (userId: string, desde: string | null, hasta: string | null) =>
    ['coordinador', 'personal-perfil', userId, desde, hasta] as const,
  directivoDashboard: (cadencia: 'hoy' | 'semana' | 'mes') =>
    ['directivo', 'dashboard', cadencia] as const,
  directivoResumen: (desde: string, hasta: string, desdeAnt: string | null) =>
    ['directivo', 'resumen', desde, hasta, desdeAnt ?? 'sin-anterior'] as const,
  directivoResumenDetalle: (
    desde: string,
    hasta: string,
    granularidad: string,
    turno: number | null,
  ) => ['directivo', 'resumen-detalle', desde, hasta, granularidad, turno ?? 'todos'] as const,
  directivoLineas: (desde: string, hasta: string, turno: number | null) =>
    ['directivo', 'lineas', desde, hasta, turno ?? 'todos'] as const,
  directivoFacturacion: (desde: string, hasta: string, plantaId: string | null) =>
    ['directivo', 'facturacion', desde, hasta, plantaId ?? 'todas'] as const,
  directivoPersonas: (
    desde: string,
    hasta: string,
    plantaId: string | null,
    turno: number | null,
    areaId: string | null,
  ) =>
    [
      'directivo',
      'personas',
      desde,
      hasta,
      plantaId ?? 'todas',
      turno ?? 'todos',
      areaId ?? 'todas',
    ] as const,
  directivoResumenSemanal: (semana: string | null) =>
    ['directivo', 'resumen-semanal', semana ?? 'actual'] as const,
  directivoAnalisisMensual: (mes: string | null) =>
    ['directivo', 'analisis-mensual', mes ?? 'actual'] as const,
  directivoAnalisisCliente: (mes: string | null) =>
    ['directivo', 'analisis-cliente', mes ?? 'actual'] as const,
  directivoAuditoria: (q: AuditoriaQuery) => ['directivo', 'auditoria', q] as const,
  adminAuditoria: (q: AuditoriaQuery) => ['admin', 'auditoria', q] as const,
  adminTenant: () => ['admin', 'tenant'] as const,
  adminClientes: () => ['admin', 'clientes'] as const,
  adminCausales: () => ['admin', 'causales'] as const,
  adminCategoriasParo: () => ['admin', 'categorias-paro'] as const,
  adminEstructura: () => ['admin', 'estructura'] as const,
  adminUsuarios: () => ['admin', 'usuarios'] as const,
  superTenants: () => ['super', 'tenants'] as const,
  superTenantDetalle: (id: string) => ['super', 'tenant', id] as const,
  superTenantUsuarios: (id: string) => ['super', 'tenant', id, 'usuarios'] as const,
  me: () => ['me'] as const,
  superSalud: () => ['super', 'salud'] as const,
  superAuditoria: (q: AuditoriaQuery & { tenant_id?: string }) =>
    ['super', 'auditoria', q] as const,
};

const skusArraySchema = z.array(skuConCapacidadSchema);
const causalesTreeArraySchema = z.array(causalParoTreeNodeSchema);
const registrosArraySchema = z.array(registroProduccionHoraSchema);

export function useContextoLider() {
  return useQuery({
    queryKey: queryKeys.contextoLider(),
    queryFn: () => apiGet('/api/contexto-lider/', contextoLiderSchema),
    staleTime: 60_000,
  });
}

export function useSkusByLinea(lineaId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.skusByLinea(lineaId ?? ''),
    queryFn: () => apiGet(`/api/lineas/${lineaId}/skus`, skusArraySchema),
    enabled: Boolean(lineaId),
    staleTime: 5 * 60_000,
  });
}

export function useCausalesParo() {
  return useQuery({
    queryKey: queryKeys.causalesParo(),
    queryFn: () => apiGet('/api/causales-paro/', causalesTreeArraySchema),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * Horas del turno del líder. `fuenteCaptura` de la línea (contexto-lider): con
 * línea híbrida o de máquina se consulta cada 10 s (R12); si no, cada 30 s.
 */
export function useRegistrosTurnoHoy(
  turnoId: string | null | undefined,
  fuenteCaptura?: string | null,
) {
  return useQuery({
    queryKey: queryKeys.registrosTurnoHoy(turnoId ?? ''),
    queryFn: () => apiGet(`/api/registros/turno-hoy?turno_id=${turnoId}`, registrosArraySchema),
    enabled: Boolean(turnoId),
    staleTime: 30_000,
    refetchInterval: intervaloCaptura(fuenteCaptura),
  });
}

// ── Tiendas (captura por corrida) ──────────────────────────────────────────
const tiendasArraySchema = z.array(tiendaSchema);
const tiendaSkusArraySchema = z.array(tiendaSkuSchema);
const corridasTiendaArraySchema = z.array(registroTiendaSchema);

export function useTiendas(incluirInactivas = false) {
  return useQuery({
    queryKey: queryKeys.tiendas(incluirInactivas),
    queryFn: () =>
      apiGet(
        `/api/tiendas/${incluirInactivas ? '?incluir_inactivas=true' : ''}`,
        tiendasArraySchema,
      ),
    staleTime: 5 * 60_000,
  });
}

export function useCrearTienda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TiendaCreate) =>
      apiPost('/api/tiendas/', tiendaCreateSchema.parse(input), tiendaSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tiendas'] }),
  });
}

export function useActualizarTienda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: TiendaUpdate }) =>
      apiPatch(`/api/tiendas/${args.id}`, args.patch, tiendaSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tiendas'] }),
  });
}

export function useTiendaSkus() {
  return useQuery({
    queryKey: queryKeys.tiendaSkus(),
    queryFn: () => apiGet('/api/tiendas/skus', tiendaSkusArraySchema),
    staleTime: 5 * 60_000,
  });
}

const tiendaCapacidadesArraySchema = z.array(tiendaSkuCatalogoSchema);

/** Curación SKU×tienda (qué SKUs aplican a cada destino + sticker/termo). */
export function useTiendaCapacidades() {
  return useQuery({
    queryKey: queryKeys.tiendaCapacidades(),
    queryFn: () => apiGet('/api/tiendas/capacidades', tiendaCapacidadesArraySchema),
    staleTime: 5 * 60_000,
  });
}

export function useCrearTiendaSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TiendaSkuCreate) =>
      apiPost(
        '/api/tiendas/capacidades',
        tiendaSkuCreateSchema.parse(input),
        tiendaSkuCatalogoSchema,
      ),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.tiendaCapacidades() }),
  });
}

export function useActualizarTiendaSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: TiendaSkuUpdate }) =>
      apiPatch(`/api/tiendas/capacidades/${args.id}`, args.patch, tiendaSkuCatalogoSchema),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.tiendaCapacidades() }),
  });
}

export function useEliminarTiendaSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/tiendas/capacidades/${id}`),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.tiendaCapacidades() }),
  });
}

export function useRegistrosTiendaHoy(tiendaId?: string | null) {
  const qs = tiendaId ? `?tienda_id=${tiendaId}` : '';
  return useQuery({
    queryKey: queryKeys.registrosTiendaHoy(tiendaId ?? ''),
    queryFn: () => apiGet(`/api/registros-tienda/hoy${qs}`, corridasTiendaArraySchema),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useCrearCorridasTiendaBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    // Valida client-side antes de mandar (el backend re-valida + autoriza).
    mutationFn: (payload: RegistrosTiendaBulkCreate) => {
      const parsed = registrosTiendaBulkCreateSchema.parse(payload);
      return apiPost('/api/registros-tienda/', parsed, corridasTiendaArraySchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['registros-tienda'] });
    },
  });
}

/** Editar una corrida (líder dueño, dentro de la ventana). */
export function useEditarCorridaTienda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: RegistroTiendaEditar }) =>
      apiPatch(
        `/api/registros-tienda/${args.id}`,
        registroTiendaEditarSchema.parse(args.patch),
        registroTiendaSchema,
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['registros-tienda'] }),
  });
}

/** Invalidar (soft-delete) una corrida. razón ≥20. */
export function useInvalidarCorridaTienda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; razon: RegistroTiendaInvalidar }) =>
      apiPatch(
        `/api/registros-tienda/${args.id}/invalidar`,
        registroTiendaInvalidarSchema.parse(args.razon),
        registroTiendaSchema,
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['registros-tienda'] }),
  });
}

/** Corridas de tienda del scope del supervisor en una fecha (default hoy). */
export function useCorridasTiendaSupervisor(fecha?: string) {
  const qs = fecha ? `?fecha=${fecha}` : '';
  return useQuery({
    queryKey: ['registros-tienda', 'supervisor', fecha ?? 'hoy'] as const,
    queryFn: () => apiGet(`/api/registros-tienda/supervisor${qs}`, corridasTiendaArraySchema),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/** El supervisor valida una corrida (misma lógica que las capturas de líneas). */
export function useValidarCorridaTienda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPatch(`/api/registros-tienda/${id}/validar`, {}, registroTiendaSchema),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['registros-tienda'] }),
  });
}

export function useSupervisorDashboard() {
  return useQuery({
    queryKey: queryKeys.supervisorDashboard(),
    queryFn: () => apiGet('/api/supervisor/dashboard', supervisorDashboardSchema),
    staleTime: 30_000,
    // R12: 15 s si alguna línea del scope está conectada a máquina; si no, 30 s.
    refetchInterval: (query) => intervaloSupervisor(query.state.data),
  });
}

function buildResumenHoraQuery(p: {
  fecha?: string;
  turnoNumero?: number | null;
  soloValidados?: boolean;
}) {
  const sp = new URLSearchParams();
  if (p.fecha) sp.set('fecha', p.fecha);
  if (p.turnoNumero != null) sp.set('turno_numero', String(p.turnoNumero));
  if (p.soloValidados) sp.set('solo_validados', '1');
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * S9 — resumen hora a hora por línea del scope del supervisor. `turnoNumero`
 * undefined/null → el backend resuelve el turno programado hoy. Trae TODO
 * (validado + pendiente) para que en pantalla se vea qué falta validar.
 */
export function useSupervisorResumenHora(fecha?: string, turnoNumero?: number | null) {
  const qs = buildResumenHoraQuery({ fecha, turnoNumero });
  return useQuery({
    queryKey: ['supervisor', 'resumen-hora', fecha ?? 'hoy', turnoNumero ?? 'auto'] as const,
    queryFn: () => apiGet(`/api/supervisor/resumen-hora${qs}`, supervisorResumenHoraSchema),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/**
 * Fetch imperativo del pivote SOLO VALIDADO (para la foto que se comparte al
 * grupo). No es un hook: se llama on-demand desde el botón de descarga.
 */
export function fetchResumenHoraValidado(fecha: string, turnoNumero: number | null) {
  const qs = buildResumenHoraQuery({ fecha, turnoNumero, soloValidados: true });
  return apiGet(`/api/supervisor/resumen-hora${qs}`, supervisorResumenHoraSchema);
}

/**
 * S10 — bloques sin validar del scope del supervisor en un rango de fechas
 * (default: día operativo), con filtros opcionales por turno y línea. Marca
 * qué bloques retienen la facturación de una OP terminada (DN4).
 */
export function useSupervisorPendientes(params: {
  desde?: string;
  hasta?: string;
  turnoNumero?: number | null;
  lineaId?: string | null;
}) {
  const sp = new URLSearchParams();
  if (params.desde) sp.set('desde', params.desde);
  if (params.hasta) sp.set('hasta', params.hasta);
  if (params.turnoNumero != null) sp.set('turno_numero', String(params.turnoNumero));
  if (params.lineaId) sp.set('linea_id', params.lineaId);
  const qs = sp.toString();
  return useQuery({
    queryKey: [
      'supervisor',
      'pendientes',
      params.desde ?? 'hoy',
      params.hasta ?? 'hoy',
      params.turnoNumero ?? 'todos',
      params.lineaId ?? 'todas',
    ] as const,
    queryFn: () =>
      apiGet(`/api/supervisor/pendientes${qs ? `?${qs}` : ''}`, supervisorPendientesSchema),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useSupervisorLineaDetalle(lineaId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.supervisorLineaDetalle(lineaId ?? ''),
    queryFn: () => apiGet(`/api/supervisor/linea/${lineaId}`, supervisorLineaDetalleSchema),
    enabled: Boolean(lineaId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useSupervisorHistorico(periodo: 'ayer' | 'tres_dias' | 'semana' | 'mes') {
  return useQuery({
    queryKey: queryKeys.supervisorHistorico(periodo),
    queryFn: () =>
      apiGet(`/api/supervisor/historico?periodo=${periodo}`, supervisorHistoricoSchema),
    staleTime: 60_000,
  });
}

export function useSupervisorMisAcciones(dias: number) {
  return useQuery({
    queryKey: queryKeys.supervisorMisAcciones(dias),
    queryFn: () => apiGet(`/api/supervisor/mis-acciones?dias=${dias}`, supervisorMisAccionesSchema),
    staleTime: 30_000,
  });
}

/**
 * Dashboard del coordinador (C1). Acepta una cadencia (hoy/semana/mes) o un
 * rango explícito {desde, hasta}. Si vienen ambos `desde` y `hasta`, mandan
 * sobre la cadencia (el backend los prioriza). Mantiene compatibilidad: llamar
 * sin args equivale a cadencia 'hoy'.
 */
export function useCoordinadorDashboard(
  q: {
    cadencia?: 'hoy' | 'semana' | 'mes';
    desde?: string;
    hasta?: string;
    planta_id?: string;
  } = {},
) {
  const usaRango = Boolean(q.desde && q.hasta);
  const cadencia = q.cadencia ?? 'hoy';
  const queryKeyArg = usaRango
    ? { desde: q.desde, hasta: q.hasta, planta_id: q.planta_id }
    : { cadencia, planta_id: q.planta_id };
  return useQuery({
    queryKey: queryKeys.coordinadorDashboard(queryKeyArg),
    queryFn: () => {
      const params = new URLSearchParams();
      if (usaRango) {
        params.set('desde', q.desde!);
        params.set('hasta', q.hasta!);
      } else {
        params.set('cadencia', cadencia);
      }
      // Filtro por planta (drill del directivo / coordinador multi-planta)
      if (q.planta_id) params.set('planta_id', q.planta_id);
      return apiGet(`/api/coordinador/dashboard?${params.toString()}`, coordinadorDashboardSchema);
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    // Mantener los datos previos al cambiar de período/planta evita el flash
    // "Cargando dashboard…" que desmontaba toda la vista (QA D2).
    placeholderData: keepPreviousData,
  });
}

/**
 * Detalle hora-a-hora de una línea para el modal del C1 (drill-down desde la
 * grilla). Solo dispara cuando el modal está abierto (enabled) y hay lineaId +
 * turnoId. `fecha` opcional (default backend = CURRENT_DATE).
 */
export function useCoordinadorLineaDetalle(
  lineaId: string | null,
  turnoId: string | null,
  fecha: string | null = null,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.coordinadorLineaDetalle(lineaId ?? '', turnoId ?? '', fecha),
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('turno_id', turnoId!);
      if (fecha) params.set('fecha', fecha);
      return apiGet(
        `/api/coordinador/linea/${lineaId}/detalle?${params.toString()}`,
        coordinadorLineaDetalleSchema,
      );
    },
    enabled: (opts?.enabled ?? true) && Boolean(lineaId) && Boolean(turnoId),
    staleTime: 30_000,
  });
}

/**
 * "Línea en el tiempo" — agregación temporal de UNA línea sobre un período.
 * Lee la respuesta completa (KPIs, semáforo, diagnóstico, serie, SKUs, paros).
 * Solo dispara cuando hay lineaId. `comparar` agrega el bloque del período
 * anterior simétrico.
 */
export function useCoordinadorLineaTiempo(lineaId: string | null, q: CoordinadorLineaTiempoQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorLineaTiempo(lineaId ?? '', q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.desde) params.set('desde', q.desde);
      if (q.hasta) params.set('hasta', q.hasta);
      if (q.granularidad) params.set('granularidad', q.granularidad);
      if (q.comparar) params.set('comparar', 'true');
      const qs = params.toString();
      return apiGet(
        `/api/coordinador/linea/${lineaId}/tiempo${qs ? `?${qs}` : ''}`,
        coordinadorLineaTiempoSchema,
      );
    },
    enabled: Boolean(lineaId),
    staleTime: 60_000,
  });
}

export function useDirectivoDashboard(cadencia: 'hoy' | 'semana' | 'mes' = 'hoy') {
  return useQuery({
    queryKey: queryKeys.directivoDashboard(cadencia),
    queryFn: () =>
      apiGet(`/api/directivo/dashboard?cadencia=${cadencia}`, directivoDashboardSchema),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useDirectivoResumen(args: {
  desde: string;
  hasta: string;
  desdeAnt: string | null;
  hastaAnt: string | null;
}) {
  const { desde, hasta, desdeAnt, hastaAnt } = args;
  const ant = desdeAnt && hastaAnt ? `&desde_ant=${desdeAnt}&hasta_ant=${hastaAnt}` : '';
  return useQuery({
    queryKey: queryKeys.directivoResumen(desde, hasta, desdeAnt),
    queryFn: () =>
      apiGet(`/api/directivo/resumen?desde=${desde}&hasta=${hasta}${ant}`, directivoResumenSchema),
    staleTime: 30_000,
    refetchInterval: 60_000,
    // Al navegar períodos, conserva la vista anterior mientras carga.
    placeholderData: keepPreviousData,
  });
}

export function useDirectivoResumenDetalle(args: {
  desde: string;
  hasta: string;
  granularidad: GranularidadDetalle;
  turno: number | null;
}) {
  const { desde, hasta, granularidad, turno } = args;
  const turnoQ = turno !== null ? `&turno=${turno}` : '';
  return useQuery({
    queryKey: queryKeys.directivoResumenDetalle(desde, hasta, granularidad, turno),
    queryFn: () =>
      apiGet(
        `/api/directivo/resumen-detalle?desde=${desde}&hasta=${hasta}&granularidad=${granularidad}${turnoQ}`,
        directivoResumenDetalleSchema,
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useDirectivoLineas(args: { desde: string; hasta: string; turno: number | null }) {
  const { desde, hasta, turno } = args;
  return useQuery({
    queryKey: queryKeys.directivoLineas(desde, hasta, turno),
    queryFn: () =>
      apiGet(
        `/api/directivo/lineas?desde=${desde}&hasta=${hasta}${turno !== null ? `&turno=${turno}` : ''}`,
        directivoLineasSchema,
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useDirectivoPersonas(args: {
  desde: string;
  hasta: string;
  plantaId: string | null;
  turno: number | null;
  areaId: string | null;
}) {
  const { desde, hasta, plantaId, turno, areaId } = args;
  const extra =
    (plantaId !== null ? `&planta_id=${plantaId}` : '') +
    (turno !== null ? `&turno=${turno}` : '') +
    (areaId !== null ? `&area_id=${areaId}` : '');
  return useQuery({
    queryKey: queryKeys.directivoPersonas(desde, hasta, plantaId, turno, areaId),
    queryFn: () =>
      apiGet(
        `/api/directivo/personas?desde=${desde}&hasta=${hasta}${extra}`,
        directivoPersonasSchema,
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useDirectivoFacturacion(args: {
  desde: string;
  hasta: string;
  plantaId: string | null;
}) {
  const { desde, hasta, plantaId } = args;
  return useQuery({
    queryKey: queryKeys.directivoFacturacion(desde, hasta, plantaId),
    queryFn: () =>
      apiGet(
        `/api/directivo/facturacion?desde=${desde}&hasta=${hasta}${plantaId !== null ? `&planta_id=${plantaId}` : ''}`,
        directivoFacturacionSchema,
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useDirectivoResumenSemanal(semana: string | null = null) {
  return useQuery({
    queryKey: queryKeys.directivoResumenSemanal(semana),
    queryFn: () =>
      apiGet(
        `/api/directivo/resumen-semanal${semana ? `?semana=${semana}` : ''}`,
        directivoResumenSemanalSchema,
      ),
    staleTime: 60_000,
    // Al navegar semanas, conserva la vista anterior mientras carga (sin flash de spinner).
    placeholderData: keepPreviousData,
  });
}

export function useDirectivoAnalisisMensual(mes: string | null = null) {
  return useQuery({
    queryKey: queryKeys.directivoAnalisisMensual(mes),
    queryFn: () =>
      apiGet(
        `/api/directivo/analisis-mensual${mes ? `?mes=${mes}` : ''}`,
        directivoAnalisisMensualSchema,
      ),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useDirectivoAnalisisCliente(mes: string | null = null) {
  return useQuery({
    queryKey: queryKeys.directivoAnalisisCliente(mes),
    queryFn: () =>
      apiGet(
        `/api/directivo/analisis-cliente${mes ? `?mes=${mes}` : ''}`,
        directivoAnalisisClienteSchema,
      ),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useCoordinadorProgramacionSemana(semanaIso: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorProgramacionSemana(semanaIso),
    queryFn: () => {
      const q = semanaIso ? `?semana_iso=${semanaIso}` : '';
      return apiGet(
        `/api/coordinador/programacion-lideres${q}`,
        coordinadorProgramacionSemanaSchema,
      );
    },
    staleTime: 60_000,
  });
}

export function useCrearProgramacionLider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramacionLiderCreate) => {
      const parsed = programacionLiderCreateSchema.parse(input);
      return apiPost(
        '/api/coordinador/programacion-lideres',
        parsed,
        programacionLiderEnriquecidaSchema.partial().passthrough(),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

export function useBorrarProgramacionLider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/coordinador/programacion-lideres/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

export function useMoverProgramacionLider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: ProgramacionLiderPatch }) => {
      const parsed = programacionLiderPatchSchema.parse(args.patch);
      return apiPatch(
        `/api/coordinador/programacion-lideres/${args.id}`,
        parsed,
        programacionLiderEnriquecidaSchema.partial().passthrough(),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

export function useCopiarSemanaAnterior() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CopiarSemanaInput) => {
      const parsed = copiarSemanaInputSchema.parse(input);
      return apiPost(
        '/api/coordinador/programacion-lideres/copiar-semana',
        parsed,
        copiarSemanaResponseSchema,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

export function useCoordinadorProgramacionSupervisoresSemana(semanaIso: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorProgramacionSupervisores(semanaIso),
    queryFn: () => {
      const q = semanaIso ? `?semana_iso=${semanaIso}` : '';
      return apiGet(
        `/api/coordinador/programacion-supervisores${q}`,
        coordinadorProgramacionSupervisoresSemanaSchema,
      );
    },
    staleTime: 60_000,
  });
}

export function useCrearProgramacionSupervisor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramacionSupervisorCreate) => {
      const parsed = programacionSupervisorCreateSchema.parse(input);
      return apiPost(
        '/api/coordinador/programacion-supervisores',
        parsed,
        programacionSupervisorEnriquecidaSchema.partial().passthrough(),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

export function useBorrarProgramacionSupervisor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/coordinador/programacion-supervisores/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

export function useCopiarSemanaAnteriorSupervisores() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramacionSupervisoresCopiarInput) => {
      const parsed = programacionSupervisoresCopiarInputSchema.parse(input);
      return apiPost(
        '/api/coordinador/programacion-supervisores/copiar-semana',
        parsed,
        programacionSupervisoresCopiarResponseSchema,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

// =============================================================================
// C4 — Programaciones de producción + pedidos extra
// =============================================================================

export function useCoordinadorProgramaciones(semanaIso: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorProgramaciones(semanaIso),
    queryFn: () => {
      const q = semanaIso ? `?semana_iso=${semanaIso}` : '';
      return apiGet(
        `/api/coordinador/programaciones${q}`,
        coordinadorProgramacionProduccionSemanaSchema,
      );
    },
    staleTime: 60_000,
  });
}

export function useProgramacionDetalle(id: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorProgramacion(id ?? ''),
    queryFn: () => apiGet(`/api/coordinador/programaciones/${id}`, programacionConDetallesSchema),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useProgramacionDiff(from: string | null, to: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorProgramacionDiff(from ?? '', to ?? ''),
    queryFn: () =>
      apiGet(`/api/coordinador/programaciones/diff?from=${from}&to=${to}`, programacionDiffSchema),
    enabled: Boolean(from && to && from !== to),
    staleTime: 60_000,
  });
}

export function useCrearProgramacion() {
  const queryClient = useQueryClient();
  return useMutation({
    // z.input: `origen` es opcional en la entrada (default 'manual' al parsear),
    // así el alta manual no lo manda y el importador pasa 'excel_alpina'.
    mutationFn: (input: z.input<typeof programacionCreateSchema>) => {
      const parsed = programacionCreateSchema.parse(input);
      return apiPost(
        '/api/coordinador/programaciones',
        parsed,
        // Backend devuelve { id, version }
        z.object({ id: z.string().uuid(), version: z.number().int() }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

export function useCoordinadorPedidosExtra(semanaIso: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorPedidosExtra(semanaIso),
    queryFn: () => {
      const q = semanaIso ? `?semana_iso=${semanaIso}` : '';
      return apiGet(`/api/coordinador/pedidos-extra${q}`, pedidosExtraListSchema);
    },
    staleTime: 60_000,
  });
}

export function useCrearPedidoExtra() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PedidoExtraCreate) => {
      const parsed = pedidoExtraCreateSchema.parse(input);
      return apiPost(
        '/api/coordinador/pedidos-extra',
        parsed,
        z.object({
          id: z.string().uuid(),
          tipo: z.enum(['evento_adicional', 'nueva_version_programa']),
          programacion_relacionada_id: z.string().uuid().nullable(),
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador'] });
    },
  });
}

// =============================================================================
// C5 — Análisis de cumplimiento
// =============================================================================

function cumplimientoQS(q: CumplimientoQuery): string {
  const params = new URLSearchParams();
  if (q.desde) params.set('desde', q.desde);
  if (q.hasta) params.set('hasta', q.hasta);
  if (q.granularidad) params.set('granularidad', q.granularidad);
  if (q.agrupacion) params.set('agrupacion', q.agrupacion);
  if (q.cliente_id) params.set('cliente_id', q.cliente_id);
  if (q.planta_id) params.set('planta_id', q.planta_id);
  if (q.area_id) params.set('area_id', q.area_id);
  if (q.linea_id) params.set('linea_id', q.linea_id);
  if (q.sku_id) params.set('sku_id', q.sku_id);
  if (q.turno_numero != null) params.set('turno_numero', String(q.turno_numero));
  if (q.comparar) params.set('comparar', 'true');
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCoordinadorCumplimiento(q: CumplimientoQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorCumplimiento(q),
    queryFn: () =>
      apiGet(`/api/coordinador/cumplimiento${cumplimientoQS(q)}`, coordinadorCumplimientoSchema),
    staleTime: 60_000,
  });
}

export function useCoordinadorCumplimientoLinea(
  lineaId: string | null,
  desde: string | null,
  hasta: string | null,
) {
  return useQuery({
    queryKey: queryKeys.coordinadorCumplimientoLinea(lineaId ?? '', desde, hasta),
    queryFn: () => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      const qs = params.toString();
      return apiGet(
        `/api/coordinador/cumplimiento/linea/${lineaId}${qs ? `?${qs}` : ''}`,
        coordinadorCumplimientoLineaSchema,
      );
    },
    enabled: Boolean(lineaId),
    staleTime: 60_000,
  });
}

/** URL del CSV — el FE la usa con <a download> o window.open(...). */
export function cumplimientoExportUrl(q: CumplimientoQuery): string {
  return `/api/coordinador/cumplimiento/export${cumplimientoQS(q)}`;
}

// =============================================================================
// C6 — Análisis de líneas
// =============================================================================

function analisisLineasQS(q: AnalisisLineasQuery): string {
  const params = new URLSearchParams();
  if (q.desde) params.set('desde', q.desde);
  if (q.hasta) params.set('hasta', q.hasta);
  if (q.granularidad) params.set('granularidad', q.granularidad);
  if (q.ordenar_por) params.set('ordenar_por', q.ordenar_por);
  if (q.cliente_id) params.set('cliente_id', q.cliente_id);
  if (q.planta_id) params.set('planta_id', q.planta_id);
  if (q.area_id) params.set('area_id', q.area_id);
  if (q.comparar) params.set('comparar', 'true');
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCoordinadorAnalisisLineas(q: AnalisisLineasQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorAnalisisLineas(q),
    queryFn: () =>
      apiGet(
        `/api/coordinador/analisis-lineas${analisisLineasQS(q)}`,
        coordinadorAnalisisLineasSchema,
      ),
    staleTime: 60_000,
  });
}

export function useCoordinadorAnalisisLineaParos(
  lineaId: string | null,
  desde: string | null,
  hasta: string | null,
  opts: { soloAfectaDisponibilidad?: boolean } = {},
) {
  const soloAfecta = Boolean(opts.soloAfectaDisponibilidad);
  return useQuery({
    queryKey: [
      ...queryKeys.coordinadorAnalisisLineaParos(lineaId ?? '', desde, hasta),
      soloAfecta ? 'afecta' : 'total',
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      if (soloAfecta) params.set('solo_afecta_disponibilidad', 'true');
      const qs = params.toString();
      return apiGet(
        `/api/coordinador/analisis-lineas/${lineaId}/paros${qs ? `?${qs}` : ''}`,
        coordinadorAnalisisLineaParosSchema,
      );
    },
    enabled: Boolean(lineaId),
    staleTime: 60_000,
  });
}

export function useValidarRegistro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (registroId: string) =>
      apiPatch(`/api/registros/${registroId}/validar`, {}, registroProduccionHoraSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supervisor'] });
    },
  });
}

export function useResolverIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { incidenciaId: string; accion_tomada: string }) =>
      apiPatch(
        `/api/supervisor/incidencias/${input.incidenciaId}/resolver`,
        { accion_tomada: input.accion_tomada },
        supervisorIncidenciaResueltaSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supervisor'] });
    },
  });
}

// ── S3/S4 — Bandeja de incidencias ──────────────────────────────────────────

/** Bandeja S3: incidencias del scope con filtros (default: activas, últimos 7 días). */
export function useSupervisorIncidencias(query: SupervisorIncidenciasQuery) {
  const qs = new URLSearchParams(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== '') as [string, string][],
  ).toString();
  return useQuery({
    queryKey: ['supervisor', 'incidencias', qs] as const,
    queryFn: () =>
      apiGet(`/api/supervisor/incidencias${qs ? `?${qs}` : ''}`, supervisorIncidenciasSchema),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** Detalle S4: contexto + registro relacionado + líder + asignables. */
export function useSupervisorIncidenciaDetalle(id: string | null) {
  return useQuery({
    queryKey: ['supervisor', 'incidencias', 'detalle', id] as const,
    queryFn: () => apiGet(`/api/supervisor/incidencias/${id}`, supervisorIncidenciaDetalleSchema),
    enabled: id !== null,
  });
}

export function useDescartarIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { incidenciaId: string; razon: string }) =>
      apiPatch(
        `/api/supervisor/incidencias/${input.incidenciaId}/descartar`,
        { razon: input.razon },
        supervisorIncidenciaResueltaSchema,
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['supervisor'] }),
  });
}

export function useAsignarIncidencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { incidenciaId: string; asignada_a_user_id: string }) =>
      apiPatch(
        `/api/supervisor/incidencias/${input.incidenciaId}/asignar`,
        { asignada_a_user_id: input.asignada_a_user_id },
        supervisorIncidenciaItemSchema,
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['supervisor'] }),
  });
}

export function useResolverIncidenciasMasivo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SupervisorResolverMasivo) =>
      apiPost(
        '/api/supervisor/incidencias/resolver-masivo',
        supervisorResolverMasivoSchema.parse(input),
        supervisorResolverMasivoResultSchema,
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['supervisor'] }),
  });
}

export function useInvalidarRegistro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { registroId: string; razon: string }) =>
      apiPatch(
        `/api/registros/${input.registroId}/invalidar`,
        { razon_invalidacion: input.razon },
        registroProduccionHoraSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supervisor'] });
    },
  });
}

// Respuesta de POST /api/registros: objeto plano (1 tramo, no-regresión) o
// `{ celda, tramos: [...] }` (2+ tramos, multi-sku-por-hora Lote 3/4).
const registroCreadoSchema = z.union([registroProduccionHoraSchema, bloqueRegistradoSchema]);

export function useCrearRegistro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RegistroBloqueCreate) => {
      // Validar input client-side antes de mandar al servidor — evita roundtrip
      // si hay un bug obvio en el form. El backend re-valida.
      const parsed = registroBloqueCreateSchema.parse(payload);
      return apiPost('/api/registros/', parsed, registroCreadoSchema);
    },
    onSuccess: (_registroCreado, variables) => {
      // `variables` (el payload enviado) siempre trae `turno_id`, sin
      // importar si la respuesta fue el objeto plano o `{ celda, tramos }`.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.registrosTurnoHoy(variables.turno_id),
      });
      // Y la lista de paros abiertos (puede haber paros nuevos sin cerrar)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.parosAbiertos(variables.turno_id),
      });
    },
  });
}

// =============================================================================
// Edición de registros
// =============================================================================

/** Invalida las familias de queries afectadas por una edición. */
function invalidarEdicionRegistros(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['registros', 'turno-hoy'] });
  void qc.invalidateQueries({ queryKey: ['registros', 'historial'] });
  void qc.invalidateQueries({ queryKey: ['supervisor'] });
}

export function useEditarRegistro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { registroId: string; cambios: EditarRegistro }) => {
      const parsed = editarRegistroSchema.parse(input.cambios);
      return apiPatch(`/api/registros/${input.registroId}`, parsed, registroProduccionHoraSchema);
    },
    onSuccess: () => invalidarEdicionRegistros(qc),
  });
}

// =============================================================================
// Corrección de datos con motivo (capacidad puede_corregir_datos)
// =============================================================================

/** Tras corregir/crear, refresca las vistas del coordinador y las del registro. */
function invalidarCorreccion(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['coordinador'] });
  void qc.invalidateQueries({ queryKey: ['registros', 'turno-hoy'] });
  void qc.invalidateQueries({ queryKey: ['registros', 'historial'] });
  void qc.invalidateQueries({ queryKey: ['supervisor'] });
}

/** PATCH /api/correcciones/registros-produccion/:id — corregir registro existente. */
export function useCorregirRegistro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { registroId: string; cambios: CorreccionRegistro }) => {
      // Validación client-side (espejo del backend) — falla rápido sin roundtrip.
      const parsed = correccionRegistroSchema.parse(input.cambios);
      return apiPatch(
        `/api/correcciones/registros-produccion/${input.registroId}`,
        parsed,
        registroProduccionHoraSchema,
      );
    },
    onSuccess: () => invalidarCorreccion(qc),
  });
}

/** POST /api/correcciones/registros-produccion — alta retroactiva de un bloque que faltó. */
export function useCrearRegistroRetroactivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AltaRetroactivaRegistro) => {
      const parsed = altaRetroactivaRegistroSchema.parse(input);
      return apiPost(
        '/api/correcciones/registros-produccion',
        parsed,
        registroProduccionHoraSchema,
      );
    },
    onSuccess: () => invalidarCorreccion(qc),
  });
}

function historialQS(q: HistorialRegistrosQuery): string {
  const params = new URLSearchParams();
  if (q.desde) params.set('desde', q.desde);
  if (q.hasta) params.set('hasta', q.hasta);
  if (q.turno_id) params.set('turno_id', q.turno_id);
  if (q.incluir_invalidados) params.set('incluir_invalidados', 'true');
  params.set('limit', String(q.limit));
  params.set('offset', String(q.offset));
  return `?${params.toString()}`;
}

export function useRegistrosHistorial(filtros: HistorialRegistrosQuery) {
  return useQuery({
    queryKey: queryKeys.registrosHistorial(filtros),
    queryFn: () =>
      apiGet(`/api/registros/historial${historialQS(filtros)}`, historialRegistrosSchema),
    staleTime: 30_000,
  });
}

// =============================================================================
// Paros abiertos del líder en el turno actual
// =============================================================================

export function useParosAbiertos(turnoId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.parosAbiertos(turnoId ?? ''),
    queryFn: () => apiGet(`/api/paros/abiertos?turno_id=${turnoId}`, parosAbiertosListSchema),
    enabled: Boolean(turnoId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

/**
 * Paros del líder que MANTENIMIENTO cerró por él en el turno vigente.
 *
 * Sin esto el líder no se entera: cuando el técnico cierra, el paro desaparece
 * de `useParosAbiertos` en silencio. Mismo poll de 30s que los abiertos — es el
 * mismo banner, la misma cadencia.
 */
export function useParosCerradosPorMtto(turnoId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.parosCerradosPorMtto(turnoId ?? ''),
    queryFn: () =>
      apiGet(`/api/paros/cerrados-por-mtto?turno_id=${turnoId}`, parosCerradosPorMttoListSchema),
    enabled: Boolean(turnoId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useCerrarParo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { paroId: string; hora_fin: string; observaciones: string | null }) =>
      apiPatch(
        `/api/paros/${input.paroId}/cerrar`,
        { hora_fin: input.hora_fin, observaciones: input.observaciones },
        paroSchema,
      ),
    onSuccess: () => {
      // Invalidar todas las queries de paros abiertos y mis horas del turno —
      // no sabemos qué turno_id desde el response del paro (no incluye), así
      // que invalidamos por raíz.
      void queryClient.invalidateQueries({ queryKey: ['paros', 'abiertos'] });
      void queryClient.invalidateQueries({ queryKey: ['registros', 'turno-hoy'] });
    },
  });
}

// =============================================================================
// C8 — Análisis de paros
// =============================================================================

function analisisParosQS(q: AnalisisParosQuery): string {
  const params = new URLSearchParams();
  if (q.desde) params.set('desde', q.desde);
  if (q.hasta) params.set('hasta', q.hasta);
  if (q.cliente_id) params.set('cliente_id', q.cliente_id);
  if (q.planta_id) params.set('planta_id', q.planta_id);
  if (q.area_id) params.set('area_id', q.area_id);
  if (q.linea_id) params.set('linea_id', q.linea_id);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCoordinadorAnalisisParos(q: AnalisisParosQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorAnalisisParos(q),
    queryFn: () =>
      apiGet(
        `/api/coordinador/analisis-paros${analisisParosQS(q)}`,
        coordinadorAnalisisParosSchema,
      ),
    staleTime: 60_000,
  });
}

// =============================================================================
// C7 — análisis de personal
// =============================================================================

function analisisPersonalQS(q: AnalisisPersonalQuery): string {
  const params = new URLSearchParams();
  if (q.desde) params.set('desde', q.desde);
  if (q.hasta) params.set('hasta', q.hasta);
  if (q.planta_id) params.set('planta_id', q.planta_id);
  if (q.area_id) params.set('area_id', q.area_id);
  if (q.linea_id) params.set('linea_id', q.linea_id);
  // Filtros Robotpack (C7): turno y supervisor. Sin esto el backend nunca los
  // recibe y el filtro no surte efecto (tarjetas y tablas no cambian).
  if (q.turno_id) params.set('turno_id', q.turno_id);
  if (q.supervisor_user_id) params.set('supervisor_user_id', q.supervisor_user_id);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCoordinadorAnalisisPersonal(q: AnalisisPersonalQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorAnalisisPersonal(q),
    queryFn: () =>
      apiGet(
        `/api/coordinador/analisis-personal${analisisPersonalQS(q)}`,
        coordinadorAnalisisPersonalSchema,
      ),
    staleTime: 60_000,
    // Al cambiar de filtro NO desmontamos la página por un spinner full-screen:
    // mantenemos los datos previos mientras refetchea (evita el "parpadeo negro").
    placeholderData: keepPreviousData,
  });
}

// =============================================================================
// Entrada a Pits — indicador de min/evento por causal configurable
// =============================================================================

function entradaPitsQS(q: EntradaPitsQuery): string {
  const params = new URLSearchParams();
  if (q.desde) params.set('desde', q.desde);
  if (q.hasta) params.set('hasta', q.hasta);
  if (q.planta_id) params.set('planta_id', q.planta_id);
  if (q.area_id) params.set('area_id', q.area_id);
  if (q.linea_id) params.set('linea_id', q.linea_id);
  if (q.lider_user_id) params.set('lider_user_id', q.lider_user_id);
  if (q.turno_id) params.set('turno_id', q.turno_id);
  if (q.supervisor_user_id) params.set('supervisor_user_id', q.supervisor_user_id);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCoordinadorEntradaPits(q: EntradaPitsQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorEntradaPits(q),
    queryFn: () =>
      apiGet(`/api/coordinador/entrada-pits${entradaPitsQS(q)}`, coordinadorEntradaPitsSchema),
    staleTime: 60_000,
    // Igual que analisis-personal: al cambiar filtros mantenemos los datos
    // previos mientras refetchea (sin parpadeo de spinner full-screen).
    placeholderData: keepPreviousData,
  });
}

export function useCoordinadorMetasPits() {
  return useQuery({
    queryKey: queryKeys.coordinadorMetasPits(),
    queryFn: () => apiGet('/api/coordinador/configuracion/metas-pits', coordinadorMetasPitsSchema),
    staleTime: 30_000,
  });
}

export function useGuardarMetasPits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MetasPitsPut) =>
      apiPut('/api/coordinador/configuracion/metas-pits', body, metasPitsPutResultSchema),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorMetasPits() });
      // El indicador depende de las metas (participación + meta resuelta).
      qc.invalidateQueries({ queryKey: ['coordinador', 'entrada-pits'] });
    },
  });
}

/**
 * Perfil individual de UNA persona (líder o supervisor) sobre un período.
 * El endpoint detecta el rol del :userId y responde la sección correspondiente
 * (lider | supervisor), más bloques comunes (KPIs, comparativo, serie). Solo
 * dispara cuando hay userId.
 */
export function useCoordinadorPersonalPerfil(
  userId: string | null,
  q: { desde: string | null; hasta: string | null },
) {
  return useQuery({
    queryKey: queryKeys.coordinadorPersonalPerfil(userId ?? '', q.desde, q.hasta),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.desde) params.set('desde', q.desde);
      if (q.hasta) params.set('hasta', q.hasta);
      const qs = params.toString();
      return apiGet(
        `/api/coordinador/personal/${userId}${qs ? `?${qs}` : ''}`,
        coordinadorPersonalPerfilSchema,
      );
    },
    enabled: Boolean(userId),
    staleTime: 60_000,
  });
}

// =============================================================================
// C9 — resumen semanal
// =============================================================================

function resumenSemanalQS(q: ResumenSemanalQuery): string {
  const params = new URLSearchParams();
  if (q.semana) params.set('semana', q.semana);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCoordinadorResumenSemanal(q: ResumenSemanalQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorResumenSemanal(q),
    queryFn: () =>
      apiGet(
        `/api/coordinador/resumen-semanal${resumenSemanalQS(q)}`,
        coordinadorResumenSemanalSchema,
      ),
    staleTime: 60_000,
  });
}

/**
 * URL absoluta del endpoint CSV (para `<a download>` con session cookie).
 * En FE se descarga con fetch + Bearer porque el JWT no va por cookie.
 */
export function resumenSemanalCsvUrl(q: ResumenSemanalQuery): string {
  return `/api/coordinador/resumen-semanal.csv${resumenSemanalQS(q)}`;
}

// =============================================================================
// C10 — configuración SKUs
// =============================================================================

function skusListQS(q: SkusListQuery): string {
  const params = new URLSearchParams();
  if (q.cliente_id) params.set('cliente_id', q.cliente_id);
  if (q.activo) params.set('activo', q.activo);
  if (q.q) params.set('q', q.q);
  if (q.linea_id) params.set('linea_id', q.linea_id);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCoordinadorSkusList(q: SkusListQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorSkusList(q),
    queryFn: () =>
      apiGet(`/api/coordinador/configuracion/skus${skusListQS(q)}`, coordinadorSkusListSchema),
    staleTime: 30_000,
  });
}

export function useCoordinadorSkuDetalle(id: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorSkuDetalle(id ?? ''),
    queryFn: () => apiGet(`/api/coordinador/configuracion/skus/${id}`, coordinadorSkuDetalleSchema),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCoordinadorSkusMatriz() {
  return useQuery({
    queryKey: queryKeys.coordinadorSkusMatriz(),
    queryFn: () =>
      apiGet('/api/coordinador/configuracion/skus/matriz', coordinadorSkusMatrizSchema),
    staleTime: 30_000,
  });
}

export function useCrearSku() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SkuCreate) =>
      apiPost('/api/coordinador/configuracion/skus', body, z.object({ id: z.string().uuid() })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coordinador', 'configuracion', 'skus'] });
    },
  });
}

export function useEditarSku(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SkuUpdate) =>
      apiPatch(`/api/coordinador/configuracion/skus/${id}`, body, z.null()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coordinador', 'configuracion', 'skus'] });
    },
  });
}

export function useUpsertSkuLineaCapacidad(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineaId, body }: { lineaId: string; body: SkuLineaUpsert }) =>
      apiPut(`/api/coordinador/configuracion/skus/${skuId}/lineas/${lineaId}`, body, z.null()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorSkuDetalle(skuId) });
      qc.invalidateQueries({ queryKey: ['coordinador', 'configuracion', 'skus'] });
    },
  });
}

export function useImportarSkusLineasBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SkuLineasBulk) =>
      apiPost('/api/coordinador/configuracion/skus/lineas/bulk', body, skuLineasBulkResultSchema),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coordinador', 'configuracion', 'skus'] });
    },
  });
}

export function useEliminarSkuLineaCapacidad(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineaId: string) =>
      apiDelete(`/api/coordinador/configuracion/skus/${skuId}/lineas/${lineaId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorSkuDetalle(skuId) });
      qc.invalidateQueries({ queryKey: ['coordinador', 'configuracion', 'skus'] });
    },
  });
}

// --- WS-5: precio del SKU + costo de mano de obra (capacidad puede_ver_costos) ---

export function useFijarPrecioSku(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PrecioUpsert) =>
      apiPost(
        `/api/coordinador/configuracion/skus/${skuId}/precio`,
        body,
        z.object({ ok: z.boolean() }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorSkuDetalle(skuId) });
      qc.invalidateQueries({ queryKey: ['coordinador', 'configuracion', 'skus'] });
    },
  });
}

export function useImportarPreciosBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PrecioBulk) =>
      apiPost('/api/coordinador/configuracion/skus/precios/bulk', body, precioBulkResultSchema),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coordinador', 'configuracion', 'skus'] });
    },
  });
}

export function useCostoManoObra(enabled = true) {
  return useQuery({
    queryKey: queryKeys.coordinadorCostoManoObra(),
    queryFn: () =>
      apiGet('/api/coordinador/configuracion/costo-mano-obra', costoManoObraResponseSchema),
    enabled,
    staleTime: 30_000,
  });
}

export function useFijarCostoManoObra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CostoManoObraUpsert) =>
      apiPost(
        '/api/coordinador/configuracion/costo-mano-obra',
        body,
        z.object({ ok: z.boolean() }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorCostoManoObra() });
    },
  });
}

export function useCostoInsumo(enabled = true) {
  return useQuery({
    queryKey: queryKeys.coordinadorCostoInsumo(),
    queryFn: () => apiGet('/api/coordinador/configuracion/costo-insumo', costoInsumoResponseSchema),
    enabled,
    staleTime: 30_000,
  });
}

export function useFijarCostoInsumo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CostoInsumoUpsert) =>
      apiPost('/api/coordinador/configuracion/costo-insumo', body, z.object({ ok: z.boolean() })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorCostoInsumo() });
    },
  });
}

// =============================================================================
// C11 — configuración turnos
// =============================================================================

export function useCoordinadorTurnosList() {
  return useQuery({
    queryKey: queryKeys.coordinadorTurnosList(),
    queryFn: () => apiGet('/api/coordinador/configuracion/turnos', coordinadorTurnosListSchema),
    staleTime: 60_000,
  });
}

export function useCrearTurno() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TurnoCreate) =>
      apiPost('/api/coordinador/configuracion/turnos', body, z.object({ id: z.string().uuid() })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorTurnosList() });
    },
  });
}

export function useEditarTurno(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TurnoUpdate) =>
      apiPatch(`/api/coordinador/configuracion/turnos/${id}`, body, z.null()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorTurnosList() });
    },
  });
}

export function useEliminarTurno() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/coordinador/configuracion/turnos/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorTurnosList() });
    },
  });
}

/** Horario por día (R6) — 7 filas vigentes de UN turno. */
export function useHorarioSemanaTurno(turnoId: string | null) {
  return useQuery({
    queryKey: queryKeys.coordinadorHorarioSemana(turnoId ?? ''),
    queryFn: () =>
      apiGet(
        `/api/coordinador/configuracion/turnos/${turnoId}/horario-semana`,
        horarioSemanaGetSchema,
      ),
    enabled: turnoId !== null,
  });
}

export function useGuardarHorarioSemanaTurno(turnoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HorarioSemanaPut) =>
      apiPut(
        `/api/coordinador/configuracion/turnos/${turnoId}/horario-semana`,
        body,
        horarioSemanaPutResponseSchema,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorHorarioSemana(turnoId) });
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorTurnosList() });
    },
  });
}

// =============================================================================
// C13 — configuración usuarios
// =============================================================================

export function useCoordinadorUsuariosList() {
  return useQuery({
    queryKey: queryKeys.coordinadorUsuariosList(),
    queryFn: () => apiGet('/api/coordinador/configuracion/usuarios', coordinadorUsuariosListSchema),
    staleTime: 60_000,
  });
}

export function useCrearUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UsuarioCreate) =>
      apiPost(
        '/api/coordinador/configuracion/usuarios',
        body,
        z.object({ user_id: z.string().uuid() }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorUsuariosList() });
    },
  });
}

export function useEditarUsuario(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UsuarioUpdate) =>
      apiPatch(`/api/coordinador/configuracion/usuarios/${userId}`, body, z.null()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorUsuariosList() });
    },
  });
}

export function useReasignarLineas(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UsuarioReasignarLineas) =>
      apiPut(`/api/coordinador/configuracion/usuarios/${userId}/lineas`, body, z.null()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorUsuariosList() });
    },
  });
}

export function useResetPasswordUsuario(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UsuarioResetPassword) =>
      apiPost(`/api/coordinador/configuracion/usuarios/${userId}/reset-password`, body, z.null()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.coordinadorUsuariosList() });
    },
  });
}

// =============================================================================
// C14 — auditoría
// =============================================================================

function auditoriaQS(q: AuditoriaQuery): string {
  const params = new URLSearchParams();
  if (q.desde) params.set('desde', q.desde);
  if (q.hasta) params.set('hasta', q.hasta);
  if (q.user_id) params.set('user_id', q.user_id);
  if (q.tipo_evento) params.set('tipo_evento', q.tipo_evento);
  if (q.entidad_tipo) params.set('entidad_tipo', q.entidad_tipo);
  if (q.q) params.set('q', q.q);
  params.set('limit', String(q.limit));
  params.set('offset', String(q.offset));
  return `?${params.toString()}`;
}

export function useCoordinadorAuditoria(q: AuditoriaQuery) {
  return useQuery({
    queryKey: queryKeys.coordinadorAuditoria(q),
    queryFn: () =>
      apiGet(`/api/coordinador/auditoria${auditoriaQS(q)}`, coordinadorAuditoriaSchema),
    staleTime: 30_000,
  });
}

export function useDirectivoAuditoria(q: AuditoriaQuery) {
  return useQuery({
    queryKey: queryKeys.directivoAuditoria(q),
    queryFn: () => apiGet(`/api/directivo/auditoria${auditoriaQS(q)}`, coordinadorAuditoriaSchema),
    staleTime: 30_000,
  });
}

export function useAdminAuditoria(q: AuditoriaQuery) {
  return useQuery({
    queryKey: queryKeys.adminAuditoria(q),
    queryFn: () => apiGet(`/api/admin/auditoria${auditoriaQS(q)}`, coordinadorAuditoriaSchema),
    staleTime: 30_000,
  });
}

// ── Admin Tenant: queries + mutations ──
const okSchema = z.object({}).passthrough();

export function useAdminTenant() {
  return useQuery({
    queryKey: queryKeys.adminTenant(),
    queryFn: () => apiGet('/api/admin/tenant', adminTenantSchema),
    staleTime: 60_000,
  });
}
export function useUpdateAdminTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => apiPatch('/api/admin/tenant', body, adminTenantSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.adminTenant() });
      // /api/me sirve la config operativa y el formato de lote (B7) al resto
      // de pantallas: se refresca para que el cambio aplique sin recargar.
      return qc.invalidateQueries({ queryKey: queryKeys.me() });
    },
  });
}

export function useAdminClientes() {
  return useQuery({
    queryKey: queryKeys.adminClientes(),
    queryFn: () => apiGet('/api/admin/clientes', adminClientesListSchema),
    staleTime: 60_000,
  });
}
export function useCreateAdminCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => apiPost('/api/admin/clientes', body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminClientes() }),
  });
}
export function useUpdateAdminCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPatch(`/api/admin/clientes/${id}`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminClientes() }),
  });
}

export function useAdminCausales() {
  return useQuery({
    queryKey: queryKeys.adminCausales(),
    queryFn: () => apiGet('/api/admin/causales', adminCausalesListSchema),
    staleTime: 60_000,
  });
}
export function useCreateAdminCausal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => apiPost('/api/admin/causales', body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminCausales() }),
  });
}
export function useUpdateAdminCausal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPatch(`/api/admin/causales/${id}`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminCausales() }),
  });
}

// ── Admin Categorías de Paro (A3-bis) ─────────────────────────────────────────
// El endpoint devuelve { categorias: [...] } (no un array pelado): validar con
// esa forma o el safeParse falla → query en error → lista vacía → todas las
// causales se ven "Sin categoría".
const categoriaParoListSchema = z.object({ categorias: z.array(categoriaParoOutputSchema) });

export function useAdminCategoriasParo() {
  return useQuery({
    queryKey: queryKeys.adminCategoriasParo(),
    queryFn: () =>
      apiGet('/api/admin/categorias-paro', categoriaParoListSchema).then((r) => r.categorias),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useCreateAdminCategoriaParo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CrearCategoriaParo) =>
      apiPost('/api/admin/categorias-paro', body, categoriaParoOutputSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminCategoriasParo() }),
  });
}

export function useUpdateAdminCategoriaParo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ActualizarCategoriaParo }) =>
      apiPatch(`/api/admin/categorias-paro/${id}`, body, categoriaParoOutputSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminCategoriasParo() }),
  });
}

// Re-export CategoriaParoOutput for convenience
export type { CategoriaParoOutput };

export function useAdminEstructura() {
  return useQuery({
    queryKey: queryKeys.adminEstructura(),
    queryFn: () => apiGet('/api/admin/estructura', adminEstructuraSchema),
    staleTime: 60_000,
  });
}
export function useCreateAdminPlanta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => apiPost('/api/admin/plantas', body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminEstructura() }),
  });
}
export function useUpdateAdminPlanta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPatch(`/api/admin/plantas/${id}`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminEstructura() }),
  });
}
export function useCreateAdminArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ plantaId, body }: { plantaId: string; body: unknown }) =>
      apiPost(`/api/admin/plantas/${plantaId}/areas`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminEstructura() }),
  });
}
export function useUpdateAdminArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPatch(`/api/admin/areas/${id}`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminEstructura() }),
  });
}
export function useCreateAdminLinea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ plantaId, body }: { plantaId: string; body: unknown }) =>
      apiPost(`/api/admin/plantas/${plantaId}/lineas`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminEstructura() }),
  });
}
export function useUpdateAdminLinea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPatch(`/api/admin/lineas/${id}`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminEstructura() }),
  });
}

export function useAdminUsuarios() {
  return useQuery({
    queryKey: queryKeys.adminUsuarios(),
    queryFn: () => apiGet('/api/admin/usuarios', adminUsuariosListSchema),
    staleTime: 30_000,
  });
}
export function useCreateAdminUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => apiPost('/api/admin/usuarios', body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminUsuarios() }),
  });
}
export function useUpdateAdminUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPatch(`/api/admin/usuarios/${id}`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminUsuarios() }),
  });
}
export function useAdminReasignarPlantas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPut(`/api/admin/usuarios/${id}/plantas`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminUsuarios() }),
  });
}
export function useAdminReasignarLineas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      apiPut(`/api/admin/usuarios/${id}/lineas`, body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminUsuarios() }),
  });
}

// ── Super Admin ──
export function useSuperTenants() {
  return useQuery({
    queryKey: queryKeys.superTenants(),
    queryFn: () => apiGet('/api/super-admin/tenants', superTenantsListSchema),
    staleTime: 30_000,
  });
}
export function useSuperTenantDetalle(id: string | null) {
  return useQuery({
    queryKey: queryKeys.superTenantDetalle(id ?? ''),
    queryFn: () => apiGet(`/api/super-admin/tenants/${id}`, superTenantDetalleSchema),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}
export function useSuperTenantUsuarios(id: string | null) {
  return useQuery({
    queryKey: queryKeys.superTenantUsuarios(id ?? ''),
    queryFn: () => apiGet(`/api/super-admin/tenants/${id}/usuarios`, superTenantUsuariosSchema),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}
/** Otorga/revoca `puede_configurar_tenant` a un directivo (solo super_admin). */
export function useSetPermisoConfigTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { tenantId: string; userId: string; puede: boolean }) =>
      apiPatch(
        `/api/super-admin/tenants/${vars.tenantId}/usuarios/${vars.userId}/permiso-config`,
        { puede_configurar_tenant: vars.puede },
        z.object({ ok: z.boolean(), puede_configurar_tenant: z.boolean() }),
      ),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.superTenantUsuarios(vars.tenantId) });
    },
  });
}
/** Refresh manual de las vistas materializadas (operación SA, mig 28). */
export function useRefreshMvs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost(
        '/api/super-admin/salud/refresh-mvs',
        {},
        z.object({ ok: z.boolean(), duracion_ms: z.number() }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.superSalud() });
    },
  });
}
/** Reset de contraseña de un usuario del tenant (operación SA, auditada). */
export function useResetPasswordSA() {
  return useMutation({
    mutationFn: (vars: { tenantId: string; userId: string; password: string }) =>
      apiPost(
        `/api/super-admin/tenants/${vars.tenantId}/usuarios/${vars.userId}/reset-password`,
        { password: vars.password },
        z.object({ ok: z.boolean() }),
      ),
  });
}
/**
 * GET /api/me — hidratación + permiso de configuración del tenant.
 * `enabled` (default true) permite diferir el fetch — ej. CopilotoFab (F2)
 * solo lo dispara cuando el rol ya calificó por el gate de auth.
 */
export function useMe(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: () => apiGet('/api/me', meResponseSchema),
    staleTime: 60_000,
    enabled: opts?.enabled ?? true,
  });
}
export function useSuperSalud() {
  return useQuery({
    queryKey: queryKeys.superSalud(),
    queryFn: () => apiGet('/api/super-admin/salud', superSaludSchema),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}
export function useSuperAuditoria(q: AuditoriaQuery & { tenant_id?: string }) {
  const qs = new URLSearchParams();
  if (q.tenant_id) qs.set('tenant_id', q.tenant_id);
  if (q.desde) qs.set('desde', q.desde);
  if (q.hasta) qs.set('hasta', q.hasta);
  if (q.tipo_evento) qs.set('tipo_evento', q.tipo_evento);
  if (q.entidad_tipo) qs.set('entidad_tipo', q.entidad_tipo);
  if (q.q) qs.set('q', q.q);
  qs.set('limit', String(q.limit));
  qs.set('offset', String(q.offset));
  return useQuery({
    queryKey: queryKeys.superAuditoria(q),
    queryFn: () => apiGet(`/api/super-admin/auditoria?${qs.toString()}`, superAuditoriaSchema),
    staleTime: 30_000,
  });
}
export function useCreateSuperTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => apiPost('/api/super-admin/tenants', body, okSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.superTenants() }),
  });
}

// ============================================================================
// WS-8 P1 — Facturación: programación por OP
// ============================================================================

export function useProgramacionOp(q: ProgramacionOpListQuery) {
  const qs = new URLSearchParams({ desde: q.desde, hasta: q.hasta });
  if (q.cliente_id) qs.set('cliente_id', q.cliente_id);
  if (q.area_id) qs.set('area_id', q.area_id);
  return useQuery({
    queryKey: ['coordinador', 'facturacion', 'programacion-op', q],
    queryFn: () =>
      apiGet(
        `/api/coordinador/facturacion/programacion-op?${qs.toString()}`,
        programacionOpListSchema,
      ),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

const programacionOpIdSchema = z.object({ id: z.string() });

export function useCrearProgramacionOp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramacionOpCreate) => {
      const parsed = programacionOpCreateSchema.parse(input);
      return apiPost(
        '/api/coordinador/facturacion/programacion-op',
        parsed,
        programacionOpIdSchema,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador', 'facturacion'] });
    },
  });
}

export function useImportarProgramacionOp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramacionOpImport) => {
      const parsed = programacionOpImportSchema.parse(input);
      return apiPost(
        '/api/coordinador/facturacion/programacion-op/importar',
        parsed,
        programacionOpImportResultSchema,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador', 'facturacion'] });
    },
  });
}

export function useCorregirProgramacionOp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: ProgramacionOpCorreccion & { id: string }) => {
      const parsed = programacionOpCorreccionSchema.parse(input);
      return apiPatch(
        `/api/coordinador/facturacion/programacion-op/${id}`,
        parsed,
        programacionOpIdSchema,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador', 'facturacion'] });
    },
  });
}

export function useEliminarProgramacionOp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, razon }: { id: string; razon: string }) =>
      apiDelete(`/api/coordinador/facturacion/programacion-op/${id}`, {
        body: JSON.stringify({ razon }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador', 'facturacion'] });
    },
  });
}

// ============================================================================
// WS-8 P2 — Facturación: cruce / conciliación
// ============================================================================

export function useFacturacionCruce(q: FacturacionCruceQuery) {
  const qs = new URLSearchParams({ desde: q.desde, hasta: q.hasta });
  if (q.cliente_id) qs.set('cliente_id', q.cliente_id);
  if (q.area_id) qs.set('area_id', q.area_id);
  return useQuery({
    queryKey: ['coordinador', 'facturacion', 'cruce', q],
    queryFn: () =>
      apiGet(`/api/coordinador/facturacion/cruce?${qs.toString()}`, facturacionCruceSchema),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCruceDetalle(q: CruceDetalleQuery | null) {
  return useQuery({
    queryKey: ['coordinador', 'facturacion', 'cruce-detalle', q],
    queryFn: () => {
      const qs = new URLSearchParams({
        desde: q!.desde,
        hasta: q!.hasta,
        cliente_id: q!.cliente_id,
        planta_id: q!.planta_id,
        sku_id: q!.sku_id,
      });
      if (q!.numero_op) qs.set('numero_op', q!.numero_op);
      return apiGet(
        `/api/coordinador/facturacion/cruce/detalle?${qs.toString()}`,
        cruceDetalleSchema,
      );
    },
    enabled: q !== null,
    staleTime: 30_000,
  });
}

export function useCerrarCruce() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CruceCerrar) => {
      const parsed = cruceCerrarSchema.parse(input);
      return apiPatch('/api/coordinador/facturacion/cruce/cerrar', parsed, cruceCerrarResultSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador', 'facturacion'] });
    },
  });
}

export function useReabrirCruce() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CruceReabrir) => {
      const parsed = cruceReabrirSchema.parse(input);
      return apiDelete('/api/coordinador/facturacion/cruce/cerrar', {
        body: JSON.stringify(parsed),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador', 'facturacion'] });
    },
  });
}

export function useNivelServicio(q: NivelServicioQuery) {
  const qs = new URLSearchParams({ desde: q.desde, hasta: q.hasta });
  if (q.cliente_id) qs.set('cliente_id', q.cliente_id);
  return useQuery({
    queryKey: ['coordinador', 'facturacion', 'nivel-servicio', q],
    queryFn: () =>
      apiGet(`/api/coordinador/facturacion/nivel-servicio?${qs.toString()}`, nivelServicioSchema),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useFacturacionQ(q: FacturacionQQuery) {
  const qs = new URLSearchParams({ desde: q.desde, hasta: q.hasta });
  if (q.cliente_id) qs.set('cliente_id', q.cliente_id);
  return useQuery({
    queryKey: ['coordinador', 'facturacion', 'q', q],
    queryFn: () => apiGet(`/api/coordinador/facturacion/q?${qs.toString()}`, facturacionQSchema),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useSetDominical() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FacturacionQDominical) => {
      const parsed = facturacionQDominicalSchema.parse(input);
      return apiPatch(
        '/api/coordinador/facturacion/q/dominical',
        parsed,
        facturacionQDominicalResultSchema,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['coordinador', 'facturacion'] });
    },
  });
}

// =============================================================================
// Ver como (suplantación de solo lectura, SA4)
// =============================================================================

/** Roster de personas suplantables del tenant (para el picker del directivo).
 *  Se pide SIEMPRE como el actor real (sinVerComo), aun estando en modo espejo:
 *  el endpoint exige rol directivo/super_admin. */
export function useVerComoRoster(enabled = true) {
  return useQuery({
    queryKey: ['ver-como', 'roster'],
    queryFn: () =>
      apiGet('/api/ver-como/usuarios', verComoRosterSchema, undefined, { sinVerComo: true }),
    enabled,
    staleTime: 60_000,
  });
}

/** Audita el inicio de una sesión de suplantación (best-effort, fire-and-forget).
 *  sinVerComo: la audita el actor, no el suplantado (evita el bloqueo de escritura). */
export function registrarVerComoInicio(userIdSuplantado: string): Promise<unknown> {
  return apiPost(
    '/api/ver-como/inicio',
    { user_id_suplantado: userIdSuplantado },
    okSchema,
    undefined,
    {
      sinVerComo: true,
    },
  );
}
